/**
 * "Pushimet (KS)" — per worker, the annual balance plus the days taken of every
 * other leave type, for one year.
 *
 * Every number comes from GET /api/reports/leave-balance?include=types, i.e.
 * backend/services/leaveSummary.js — the same figures as "Balanca e pushimeve".
 * The previous version summed /api/holidays in the browser: it counted rejected
 * and pending requests, defaulted a missing day count to 1, read types through
 * a hard-coded id map that no longer matched `leave_types`, ignored carry-over
 * and corrections, and listed every worker ever hired.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Grid, TextField, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, Button, LinearProgress, Stack, Typography, Chip, Paper, TableContainer, Alert } from '@mui/material';
import { printReport, textCol, numCol } from '../../utils/pdfPrint';
import apiService, { authFetch } from '../../services/apiServices';
import { activeWorkers } from '../../utils/workerStatus';
import PageHeader from '../common/PageHeader';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import { Link } from 'react-router-dom';
import { ReportPage, ExportMenu } from './ReportKit';

const ANNUAL_ID = 1;
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export default function LeaveReportKS() {
  const [data, setData] = useState({ rows: [], leaveTypes: [] });
  const [year, setYear] = useState(new Date().getFullYear());
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const { t } = useTranslation();

  // Email recipients only — the report rows never come from this list.
  useEffect(() => {
    apiService().get('workers')
      .then((w) => { if (w?.success) setRecipients(activeWorkers(w.data)); })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({ year: String(year), status, include: 'types' });
      const res = await authFetch(`/api/reports/leave-balance?${qs.toString()}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) {
        setError(res.status === 403
          ? 'Nuk keni leje për të parë pushimet e kompanisë.'
          : (json.error || `Nuk u morën të dhënat (HTTP ${res.status}).`));
        setData({ rows: [], leaveTypes: [] });
        return;
      }
      setData({ rows: json.data?.rows || [], leaveTypes: json.data?.leaveTypes || [] });
    } catch {
      setError('Nuk u lidh dot me serverin.');
      setData({ rows: [], leaveTypes: [] });
    } finally {
      setLoading(false);
    }
  }, [year, status]);

  useEffect(() => { load(); }, [load]);

  // Every type other than annual gets a "days taken" column, named from the
  // tenant's own `leave_types` — never from an id map in this file.
  const otherTypes = useMemo(
    () => data.leaveTypes.filter((lt) => Number(lt.Id) !== ANNUAL_ID),
    [data.leaveTypes]
  );
  const typeKey = (lt) => `t${lt.Id}`;

  const rows = useMemo(() => data.rows.map((r) => {
    const out = {
      id: r.workerId,
      name: r.workerName,
      department: r.department,
      annualAllowed: r2(r.entitlementDays),
      annualUsed: r2(r.approvedDays),
      annualPending: r2(r.pendingDays),
      annualRemain: r2(r.remainingDays),
    };
    otherTypes.forEach((lt) => { out[typeKey(lt)] = r2(r.byType?.[lt.Id]?.approvedDays); });
    return out;
  }), [data.rows, otherTypes]);

  const totals = useMemo(() => {
    const sum = (k) => r2(rows.reduce((a, r) => a + (Number(r[k]) || 0), 0));
    const out = { annualAllowed: sum('annualAllowed'), annualUsed: sum('annualUsed'), annualPending: sum('annualPending'), annualRemain: sum('annualRemain') };
    otherTypes.forEach((lt) => { out[typeKey(lt)] = sum(typeKey(lt)); });
    return out;
  }, [rows, otherTypes]);

  // Types nobody used this year stay out of the table — fifteen empty columns
  // would bury the five that matter. Exports keep them for completeness.
  const shownTypes = useMemo(() => otherTypes.filter((lt) => totals[typeKey(lt)] > 0), [otherTypes, totals]);

  const columns = useMemo(() => [
    { key: 'name', label: t('report.worker'), align: 'left' },
    { key: 'department', label: 'Departamenti', align: 'left' },
    { key: 'annualAllowed', label: t('report.annualAllowed'), align: 'right' },
    { key: 'annualUsed', label: t('report.annualUsed'), align: 'right' },
    { key: 'annualPending', label: 'Në pritje', align: 'right' },
    { key: 'annualRemain', label: t('report.annualRemain'), align: 'right' },
    ...shownTypes.map((lt) => ({ key: typeKey(lt), label: lt.Name, align: 'right' })),
  ], [t, shownTypes]);

  const exportColumns = useMemo(() => [
    ...columns.filter((c) => !/^t\d+$/.test(c.key)),
    ...otherTypes.map((lt) => ({ key: typeKey(lt), label: lt.Name, align: 'right' })),
  ], [columns, otherTypes]);

  const cellText = (v) => String(v ?? '').replace(/"/g, '""');

  function exportCsv() {
    const lines = [exportColumns.map((c) => `"${cellText(c.label)}"`).join(',')];
    rows.forEach((r) => lines.push(exportColumns.map((c) => `"${cellText(r[c.key])}"`).join(',')));
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `pushimet_ks_${year}.csv`; a.click(); URL.revokeObjectURL(url);
  }

  function exportXls() {
    const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;');
    const head = exportColumns.map((c) => `<th>${esc(c.label)}</th>`).join('');
    const body = rows.map((r) => `<tr>${exportColumns.map((c) => `<td>${esc(r[c.key])}</td>`).join('')}</tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset='UTF-8'></head><body><table border='1'><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`;
    const blob = new Blob([html], { type: 'application/vnd.ms-excel' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `pushimet_ks_${year}.xls`; a.click(); URL.revokeObjectURL(url);
  }

  function exportPdf() {
    const width = `${Math.floor(70 / Math.max(columns.length - 1, 1))}%`;
    printReport({
      title: t('reports.leaveReportKS'),
      subtitle: `${t('report.year')} ${year} — ${t('report.systemKS')}`,
      orientation: 'landscape',
      columns: columns.map((c, i) => (i === 0
        ? textCol(c.label, c.key, '22%')
        : c.align === 'left' ? textCol(c.label, c.key, '14%') : numCol(c.label, c.key, width))),
      rows,
      totals,
      period: String(year),
      badge: { label: `${rows.length} ${t('report.workers')}` },
    });
  }

  return (
    <ReportPage>
      <PageHeader
        title={t('reports.leaveReportKS')}
        actions={<Button component={Link} to="/home/reports" variant="outlined">{t('report.backLabel')}</Button>}
      />
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={3}>
              <TextField select fullWidth label={t('report.year')} size="small" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map((y) => (<MenuItem key={y} value={y}>{y}</MenuItem>))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField select fullWidth label="Statusi" size="small" value={status} onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value="active">Aktivë</MenuItem>
                <MenuItem value="inactive">Joaktivë</MenuItem>
                <MenuItem value="all">Të gjithë</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm="auto"><ExportMenu onCsv={exportCsv} onExcel={exportXls} onPdf={exportPdf} onEmail={() => setEmailOpen(true)} disabled={rows.length === 0} /></Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button size="small" onClick={load}>Provo përsëri</Button>}>{error}</Alert>}

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        {[
          { label: t('report.workers'), value: rows.length },
          { label: t('report.annualUsed'), value: `${totals.annualUsed} ${t('report.days')}` },
          { label: t('report.annualRemain'), value: `${totals.annualRemain} ${t('report.days')}` },
          ...shownTypes.slice(0, 3).map((lt) => ({ label: lt.Name, value: `${totals[typeKey(lt)]} ${t('report.days')}` })),
        ].map((item) => (
          <Card key={item.label} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', minWidth: 130 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">{item.label}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>{item.value}</Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <TableContainer component={Paper} elevation={0}>
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableCell key={c.key} align={c.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{c.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {t('report.noDataForYear', { year })}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                    <TableCell>{r.department || '—'}</TableCell>
                    <TableCell align='right'>{r.annualAllowed}</TableCell>
                    <TableCell align='right'>
                      <Chip label={r.annualUsed} size="small" color={r.annualUsed > r.annualAllowed ? 'error' : 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell align='right'>{r.annualPending || '—'}</TableCell>
                    <TableCell align='right'>
                      <Chip label={r.annualRemain} size="small" color={r.annualRemain < 0 ? 'error' : r.annualRemain <= 5 ? 'warning' : 'success'} variant="outlined" />
                    </TableCell>
                    {shownTypes.map((lt) => (
                      <TableCell key={lt.Id} align='right'>{r[typeKey(lt)] || '—'}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <SendReportEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        reportTitle={t('reports.leaveReportKS')}
        subtitle={`${t('report.year')} ${year}`}
        columns={columns}
        rows={rows}
        totals={totals}
        workers={recipients}
      />
    </ReportPage>
  );
}
